<template>
  <div
    class="segment-image-panel"
    v-if="shouldDisplayImage"
    :class="{ 'is-fullscreen': isFullscreen, 'is-playing': isPlaying, 'is-recording': isRecording }"
    tabindex="0"
  >
    <!-- Recording countdown overlay -->
    <recording-countdown
      ref="countdown"
      :duration="countdownDuration"
      @complete="onCountdownComplete"
      @cancelled="onCountdownCancelled"
    />

    <!-- Recording Error Notification -->
    <transition name="fade">
      <div v-if="recordingError" class="recording-error-notification">
        <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
          <circle cx="12" cy="12" r="10"></circle>
          <line x1="12" y1="8" x2="12" y2="12"></line>
          <line x1="12" y1="16" x2="12.01" y2="16"></line>
        </svg>
        <span>{{ recordingError }}</span>
        <button @click="recordingError = null" class="error-close-btn">&times;</button>
      </div>
    </transition>

    <div class="image-display" @click="openImageUrl(currentImage.url)">
      <div class="image-container animated">
        <div
          class="image-animation-container"
          :class="[currentAnimation, noRepeatAnimation ? 'no-repeat' : '']"
        >
          <img
            :src="currentImage.image || currentImage.thumbnail"
            :alt="currentImage.title"
            @error="handleImageError"
            class="document-image"
          />
        </div>

        <!-- Add Live Transcript component with font settings -->
        <live-transcript
          v-if="isPlaying"
          :message-id="messageId"
          :voice-speed="voiceSpeed"
          :text="activeSegmentContent"
          :speaker="activeSegmentSpeaker"
          :speaker-image="speakerImage"
          :visible="isPlaying"
          :font-family="fontSettings.fontFamily"
          :font-size="fontSettings.fontSize"
          :font-weight="fontSettings.fontWeight"
          :line-height="fontSettings.lineHeight"
          :letter-spacing="fontSettings.letterSpacing"
          :text-color="fontSettings.textColor"
          :text-shadow="fontSettings.textShadow"
        />
      </div>

      <div v-if="shouldShowIntroMontage && activeSegment === 0" class="intro-montage">
      <transition-group name="montage-transition" tag="div">
        <div
          v-for="(image, index) in montageImages"
          :key="`montage-${Date.now()}-${index}`"
          class="montage-image-container"
          :style="getMontageImageStyle(index)"
        >
          <img
            :src="image.image || image.thumbnail || randomPlaceholderImage"
            :alt="image.title || 'Montage image'"
            class="montage-image"
            @error="handleImageError"
          />
        </div>
      </transition-group>
    </div>

     <!-- Enhanced image footer with citation sources -->
    <template>
      <div class="image-footer">
        <!-- Image source -->
        <div class="image-source">
          Source: <a :href="currentImage.url" target="_blank" rel="noopener noreferrer">{{ formatUrl(currentImage.url) }}</a>
        </div>

        <!-- Citation sources when playing -->
        <div v-if="isPlaying && citationSources.length > 0" class="citation-sources">
          <div class="citation-sources-label">Citations:</div>
          <div class="citation-sources-list">
            <span v-for="(source, index) in citationSources" :key="index" class="citation-source">
              <a :href="source.url" target="_blank" rel="noopener noreferrer" class="citation-source-link">
                {{ source.domain }}
              </a>
              <span v-if="index < citationSources.length - 1" class="citation-separator">, </span>
            </span>
          </div>
        </div>
      </div>
    </template>

      <!-- UI Controls Layer - Updated to support recording mode -->
      <div class="ui-controls-layer" :class="{ 'hidden': (isPlaying && !showControls && recordingMode) || forceHideAllUI, 'recording-hidden': isRecording }">
        <!-- Fullscreen toggle -->
        <button
          v-if="!isRecording"
          @click.stop="toggleFullscreen"
          class="fullscreen-toggle"
          :class="{ 'recording-mode': recordingMode }"
          aria-label="Toggle fullscreen"
        >
          <svg v-if="!isFullscreen" xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <path d="M8 3H5a2 2 0 0 0-2 2v3"></path>
            <path d="M21 8V5a2 2 0 0 0-2-2h-3"></path>
            <path d="M3 16v3a2 2 0 0 0 2 2h3"></path>
            <path d="M16 21h3a2 2 0 0 0 2-2v-3"></path>
          </svg>
          <svg v-else xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <path d="M8 3v3a2 2 0 0 1-2 2H3"></path>
            <path d="M21 8h-3a2 2 0 0 1-2-2V3"></path>
            <path d="M3 16h3a2 2 0 0 1 2 2v3"></path>
            <path d="M16 21v-3a2 2 0 0 1 2-2h3"></path>
          </svg>
        </button>

        <!-- Large play button and record button (only visible when paused) -->
        <div v-if="!isPlaying && !isPreparingRecording" class="large-play-button-container">
          <button class="nav-button prev-button" @click.stop="skipPrevious" aria-label="Previous segment">
            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <polygon points="19 20 9 12 19 4 19 20"></polygon>
              <line x1="5" y1="19" x2="5" y2="5"></line>
            </svg>
          </button>

          <!-- Play button -->
          <button class="large-play-button" @click.stop="playOnly" aria-label="Play">
            <svg xmlns="http://www.w3.org/2000/svg" width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <polygon points="5 3 19 12 5 21 5 3"></polygon>
            </svg>
          </button>

          <!-- Record button (if recording mode is enabled) -->
          <button v-if="recordingMode" class="large-record-button" @click.stop="recordAndPlay" aria-label="Record and Play">
            <svg xmlns="http://www.w3.org/2000/svg" width="42" height="42" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <circle cx="12" cy="12" r="10"></circle>
              <circle cx="12" cy="12" r="6" fill="currentColor"></circle>
            </svg>
          </button>

          <button class="nav-button next-button" @click.stop="skipNext" aria-label="Next segment">
            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <polygon points="5 4 15 12 5 20 5 4"></polygon>
              <line x1="19" y1="5" x2="19" y2="19"></line>
            </svg>
          </button>
        </div>

        <!-- Only show animation settings when not playing and not recording -->
        <div v-if="!isPlaying && !isRecording && (isFullscreen || isMobile)" class="recording-settings">
          <!-- Settings toggle button -->
          <button @click.stop="toggleSettings" class="settings-toggle" aria-label="Toggle settings">
            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <circle cx="12" cy="12" r="3"></circle>
              <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z"></path>
            </svg>
          </button>

          <!-- Settings panel (conditionally shown) -->
          <div v-if="showSettingsPanel" class="settings-panel" @click.stop>
            <!-- Recording mode toggle -->
            <div class="settings-section">
              <label class="settings-label">
                <input type="checkbox" v-model="recordingMode" @change="saveSettings">
                <span>Recording Mode</span>
                <span class="settings-hint">(Enables tab recording with screen capture)</span>
              </label>
            </div>

            <!-- Font Settings Section -->
            <div class="font-settings-section">
              <div class="customization-label">
                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                  <polyline points="4 7 4 4 20 4 20 7"></polyline>
                  <line x1="9" y1="20" x2="15" y2="20"></line>
                  <line x1="12" y1="4" x2="12" y2="20"></line>
                </svg>
                <span>Font Settings</span>
              </div>

              <!-- Font Family -->
              <div class="font-control">
                <label>Font Family:</label>
                <select v-model="fontSettings.fontFamily" @change="saveFontSettings" class="font-select">
                  <option value="system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif">System Default</option>
                  <option value="'Helvetica Neue', Helvetica, Arial, sans-serif">Helvetica</option>
                  <option value="'Times New Roman', Times, serif">Times New Roman</option>
                  <option value="'Georgia', serif">Georgia</option>
                  <option value="'Arial', sans-serif">Arial</option>
                  <option value="'Verdana', sans-serif">Verdana</option>
                  <option value="'Inter', sans-serif">Inter</option>
                  <option value="'Roboto', sans-serif">Roboto</option>
                  <option value="'Open Sans', sans-serif">Open Sans</option>
                  <option value="'Lato', sans-serif">Lato</option>
                </select>
              </div>

              <!-- Font Size -->
              <div class="font-control">
                <label>Font Size:</label>
                <div class="range-control">
                  <input
                    type="range"
                    v-model.number="fontSettings.fontSize"
                    @input="saveFontSettings"
                    min="12"
                    max="32"
                    step="1"
                    class="font-range"
                  />
                  <span class="range-value">{{ fontSettings.fontSize }}px</span>
                </div>
              </div>

              <!-- Font Weight -->
              <div class="font-control">
                <label>Font Weight:</label>
                <select v-model.number="fontSettings.fontWeight" @change="saveFontSettings" class="font-select">
                  <option :value="300">Light (300)</option>
                  <option :value="400">Normal (400)</option>
                  <option :value="500">Medium (500)</option>
                  <option :value="600">Semi Bold (600)</option>
                  <option :value="700">Bold (700)</option>
                  <option :value="800">Extra Bold (800)</option>
                </select>
              </div>

              <!-- Line Height -->
              <div class="font-control">
                <label>Line Height:</label>
                <div class="range-control">
                  <input
                    type="range"
                    v-model.number="fontSettings.lineHeight"
                    @input="saveFontSettings"
                    min="1.0"
                    max="2.5"
                    step="0.1"
                    class="font-range"
                  />
                  <span class="range-value">{{ fontSettings.lineHeight }}</span>
                </div>
              </div>

              <!-- Letter Spacing -->
              <div class="font-control">
                <label>Letter Spacing:</label>
                <div class="range-control">
                  <input
                    type="range"
                    v-model.number="fontSettings.letterSpacing"
                    @input="saveFontSettings"
                    min="-1"
                    max="3"
                    step="0.1"
                    class="font-range"
                  />
                  <span class="range-value">{{ fontSettings.letterSpacing }}px</span>
                </div>
              </div>

              <!-- Text Color -->
              <div class="font-control">
                <label>Text Color:</label>
                <div class="color-picker-container">
                  <div class="current-color" :style="{ backgroundColor: fontSettings.textColor }"></div>
                  <input
                    type="color"
                    v-model="fontSettings.textColor"
                    class="color-picker"
                    @change="saveFontSettings"
                  />
                  <button class="reset-button" @click="resetFontSettings">Reset Fonts</button>
                </div>
              </div>

              <!-- Font Presets -->
              <div class="font-control">
                <label>Font Presets:</label>
                <div class="setting-buttons">
                  <button
                    v-for="preset in fontPresets"
                    :key="preset.name"
                    @click.stop="applyFontPreset(preset.name)"
                    class="setting-button"
                    :class="{ 'active': currentFontPreset === preset.name }"
                  >
                    {{ preset.label }}
                  </button>
                </div>
              </div>
            </div>

            <!-- Animation style selector -->
            <div class="settings-section">
              <label>Animation Style:</label>
              <div class="setting-buttons">
                <button
                  v-for="anim in availableAnimations"
                  :key="anim.value"
                  @click.stop="setAnimationStyle(anim.value)"
                  :class="{ active: animationStyle === anim.value }"
                  class="setting-button"
                >
                  {{ anim.label }}
                </button>
              </div>

              <!-- Animation options -->
              <div class="animation-options">
                <label class="settings-checkbox">
                  <input type="checkbox" v-model="holdAnimationAtEnd" @change="saveSettings">
                  <span>Hold at end position</span>
                </label>

                <label class="settings-checkbox">
                  <input type="checkbox" v-model="synchronizeWithSpeaking" @change="saveSettings">
                  <span>Match speaking duration</span>
                </label>

                <label v-if="!holdAnimationAtEnd && !synchronizeWithSpeaking" class="settings-checkbox">
                  <input type="checkbox" v-model="noRepeatAnimation" @change="saveSettings">
                  <span>Don't repeat animation</span>
                </label>
              </div>
            </div>

            <!-- Montage settings -->
            <div class="settings-section">
              <label class="settings-label">
                <input type="checkbox" v-model="enableIntroMontage" @change="saveSettings">
                <span>Intro Montage</span>
                <span class="settings-hint">(Rapid image sequence for first segment)</span>
              </label>
            </div>

            <!-- Aspect Ratio -->
            <div class="settings-section">
              <label>Aspect Ratio:</label>
              <div class="setting-buttons">
                <button
                  v-for="ratio in availableAspectRatios"
                  :key="ratio.value"
                  @click.stop="setAspectRatio(ratio.value)"
                  :class="{ active: aspectRatio === ratio.value }"
                  class="setting-button"
                >
                  {{ ratio.label }}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Keyboard shortcut overlay - Press '?' to toggle -->
      <div v-if="showKeyboardShortcuts" class="keyboard-shortcuts-overlay" @click.stop="showKeyboardShortcuts = false">
        <div class="keyboard-shortcuts-panel" @click.stop>
          <h3>Keyboard Shortcuts</h3>
          <div class="shortcut-list">
            <div class="shortcut-item">
              <span class="key">Space</span>
              <span>Play/Pause</span>
            </div>
            <div class="shortcut-item">
              <span class="key">F</span>
              <span>Toggle Fullscreen</span>
            </div>
            <div class="shortcut-item">
              <span class="key">←</span>
              <span>Previous Segment</span>
            </div>
            <div class="shortcut-item">
              <span class="key">→</span>
              <span>Next Segment</span>
            </div>
            <div class="shortcut-item">
              <span class="key">R</span>
              <span>Toggle Recording Mode</span>
            </div>
            <div class="shortcut-item" v-if="isRecording">
              <span class="key">Q</span>
              <span>Stop Recording</span>
            </div>
            <div class="shortcut-item" v-if="isRecording">
              <span class="key">P</span>
              <span>Pause/Resume Recording</span>
            </div>
            <div class="shortcut-item">
              <span class="key">?</span>
              <span>Show/Hide Shortcuts</span>
            </div>
          </div>
          <button @click.stop="showKeyboardShortcuts = false" class="close-button">Close</button>
        </div>
      </div>
    </div>
  </div>

  <!-- Empty State Placeholder (when no content is available) -->
  <div
    v-else-if="!showDocumentsPanel"
    class="empty-state-placeholder"
    :class="[aspectRatioClass, { 'is-fullscreen': isFullscreen, 'is-playing': isPlaying, 'is-recording': isRecording }]"
    tabindex="0"
  >
    <!-- Recording countdown overlay for empty state -->
    <recording-countdown
      ref="emptyStateCountdown"
      :duration="countdownDuration"
      @complete="onCountdownComplete"
      @cancelled="onCountdownCancelled"
    />

    <div class="placeholder-image-container">
      <div class="image-container animated">
        <div class="image-animation-container" :class="currentAnimation">
          <img
            :src="randomPlaceholderImage"
            alt="Placeholder Image"
            class="placeholder-image document-image"
          />
        </div>

        <!-- Add Live Transcript component to empty state with font settings -->
        <live-transcript
          v-if="isPlaying"
          :message-id="messageId"
          :voice-speed="voiceSpeed"
          :text="activeSegmentContent"
          :speaker="activeSegmentSpeaker"
          :speaker-image="speakerImage"
          :visible="isPlaying"
          :font-family="fontSettings.fontFamily"
          :font-size="fontSettings.fontSize"
          :font-weight="fontSettings.fontWeight"
          :line-height="fontSettings.lineHeight"
          :letter-spacing="fontSettings.letterSpacing"
          :text-color="fontSettings.textColor"
          :text-shadow="fontSettings.textShadow"
        />
      </div>
    </div>

    <!-- UI Controls for placeholder with recording support -->
    <div class="ui-controls-layer" :class="{ 'hidden': (isPlaying && !showControls && recordingMode) || forceHideAllUI, 'recording-hidden': isRecording }">
      <!-- Fullscreen toggle for placeholder -->
      <button
        v-if="!isRecording"
        @click.stop="toggleFullscreen"
        class="fullscreen-toggle"
        :class="{ 'recording-mode': recordingMode }"
        aria-label="Toggle fullscreen"
      >
        <svg v-if="!isFullscreen" xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
          <path d="M8 3H5a2 2 0 0 0-2 2v3"></path>
          <path d="M21 8V5a2 2 0 0 0-2-2h-3"></path>
          <path d="M3 16v3a2 2 0 0 0 2 2h3"></path>
          <path d="M16 21h3a2 2 0 0 0 2-2v-3"></path>
        </svg>
        <svg v-else xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
          <path d="M8 3v3a2 2 0 0 1-2 2H3"></path>
          <path d="M21 8h-3a2 2 0 0 1-2-2V3"></path>
          <path d="M3 16h3a2 2 0 0 1 2 2v3"></path>
          <path d="M16 21v-3a2 2 0 0 1 2-2h3"></path>
        </svg>
      </button>

      <!-- Large play button (only visible when paused) -->
      <div v-if="!isPlaying && !isPreparingRecording" class="large-play-button-container">
        <button class="nav-button prev-button" @click.stop="skipPrevious" aria-label="Previous segment">
          <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <polygon points="19 20 9 12 19 4 19 20"></polygon>
            <line x1="5" y1="19" x2="5" y2="5"></line>
          </svg>
        </button>

        <!-- Play button -->
        <button class="large-play-button" @click.stop="playOnly" aria-label="Play">
          <svg xmlns="http://www.w3.org/2000/svg" width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <polygon points="5 3 19 12 5 21 5 3"></polygon>
          </svg>
        </button>

        <!-- Record button -->
        <button v-if="recordingMode" class="large-record-button" @click.stop="recordAndPlay" aria-label="Record and Play">
          <svg xmlns="http://www.w3.org/2000/svg" width="42" height="42" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <circle cx="12" cy="12" r="10"></circle>
            <circle cx="12" cy="12" r="6" fill="currentColor"></circle>
          </svg>
        </button>

        <button class="nav-button next-button" @click.stop="skipNext" aria-label="Next segment">
          <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <polygon points="5 4 15 12 5 20 5 4"></polygon>
            <line x1="19" y1="5" x2="19" y2="19"></line>
          </svg>
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import LiveTranscript from '../components/LiveTranscript.vue';
import tabRecordingMixin from '../mixins/tabRecordingMixin';
import RecordingCountdown from '../components/recording/RecordingCountdown.vue';
import { set } from 'lodash';

export default {
  name: 'FullscreenImagePanel',
  components: {
    LiveTranscript,
    RecordingCountdown
  },
  mixins: [tabRecordingMixin],
  props: {
    messageId: {
      required: true,
    },
    voiceSpeed: {
      type: Number,
      default: 1
    },
    speakerImage: {
      type: String,
      default: '/static/Gemini_Generated_Image_uobiifuobiifuobi.jpeg'
    },
    searchResults: {
      type: Array,
      default: () => []
    },
    conversationEnded: {
      type: Boolean,
      default: false
    },
    activeSegment: {
      type: Number,
      default: 0
    },
    totalSegments: {
      type: Number,
      default: 0
    },
    showCitationPanel: {
      type: Boolean,
      default: false
    },
    showDocumentsPanel: {
      type: Boolean,
      default: false
    },
    isPlaying: {
      type: Boolean,
      default: false
    },
    activeSegmentRawText: {
      type: String,
      default: ''
    },
    activeSegmentContent: {
      type: String,
      default: ''
    },
    activeSegmentSpeaker: {
      type: String,
      default: ''
    },
    transcriptBackgroundColor: {
      type: String,
      default: 'rgba(0, 0, 0, 0.6)'
    },
    isMobile: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      enableBlurDetection: false, // Enable blur detection for images
      filteredImages: [], // Cache for filtered images
      imageQualityCache: new Map(), // Cache quality assessments
      imageQualityMin: 8, // 8/10 is the minimum quality for images to be considered good
      // Montage state
      isIntroMontage: true,
      isIntroSequencePlaying: false,
      introSequenceTimer: null,
      montageImageIndex: 0,
      introMontageTimer: null,
      montageImages: [],
      enableIntroMontage: true,

      // UI Controls
      animationDuration: 30,
      showControls: true,
      controlsTimer: null,
      showSettingsPanel: false,
      recordingMode: true,
      showKeyboardShortcuts: false,

      // Fullscreen state
      isFullscreen: false,

      // Font Settings
      fontSettings: {
        fontFamily: 'system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
        fontSize: 18,
        fontWeight: 400,
        lineHeight: 1.5,
        letterSpacing: 0.3,
        textColor: 'white',
        textShadow: '0 1px 2px rgba(0, 0, 0, 0.3)'
      },

      // Font Presets
      fontPresets: [
        {
          name: 'default',
          label: 'Default',
          settings: {
            fontFamily: 'system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
            fontSize: 18,
            fontWeight: 400,
            lineHeight: 1.5,
            letterSpacing: 0.3,
            textColor: 'white',
            textShadow: '0 1px 2px rgba(0, 0, 0, 0.3)'
          }
        },
        {
          name: 'large',
          label: 'Large',
          settings: {
            fontFamily: 'Arial, sans-serif',
            fontSize: 24,
            fontWeight: 500,
            lineHeight: 1.6,
            letterSpacing: 0.5,
            textColor: 'white',
            textShadow: '0 2px 4px rgba(0, 0, 0, 0.4)'
          }
        },
        {
          name: 'elegant',
          label: 'Elegant',
          settings: {
            fontFamily: 'Georgia, serif',
            fontSize: 20,
            fontWeight: 400,
            lineHeight: 1.7,
            letterSpacing: 0.2,
            textColor: '#f8f8f8',
            textShadow: '0 1px 3px rgba(0, 0, 0, 0.5)'
          }
        },
        {
          name: 'bold',
          label: 'Bold',
          settings: {
            fontFamily: 'Helvetica Neue, Helvetica, Arial, sans-serif',
            fontSize: 22,
            fontWeight: 700,
            lineHeight: 1.4,
            letterSpacing: 0.4,
            textColor: 'white',
            textShadow: '0 2px 4px rgba(0, 0, 0, 0.6)'
          }
        },
        {
          name: 'minimal',
          label: 'Minimal',
          settings: {
            fontFamily: 'Inter, sans-serif',
            fontSize: 16,
            fontWeight: 300,
            lineHeight: 1.8,
            letterSpacing: 0.1,
            textColor: '#f0f0f0',
            textShadow: '0 1px 2px rgba(0, 0, 0, 0.2)'
          }
        }
      ],

      currentFontPreset: 'default',

     // Animation options
     availableAnimations: [
        { value: 'random', label: 'Random' },
        { value: 'zoom-pan', label: 'Zoom & Pan' },
        { value: 'ken-burns', label: 'Ken Burns' },
        { value: 'fade-scale', label: 'Fade & Scale' },
        { value: 'gentle-float', label: 'Gentle Float' },
        { value: 'smooth-slide', label: 'Smooth Slide' },
        { value: 'hollywood-dramatic', label: 'Hollywood Dramatic' },
        { value: 'epic-reveal', label: 'Epic Reveal' },
        { value: 'aerial-sweep', label: 'Aerial Sweep' },
        { value: 'directors-cut', label: 'Director\'s Cut' },
        { value: 'epic-pan', label: 'Epic Pan' },
        { value: 'dolly-zoom', label: 'Dolly Zoom' },
      ],
      availableAspectRatios: [
        { value: '16:9', label: '16:9' },
        { value: '4:3', label: '4:3' },
        { value: '1:1', label: '1:1' },
        { value: '9:16', label: '9:16' }
      ],
      animations: [
        'zoom-pan-effect',
        'ken-burns-effect',
        'fade-and-scale',
        'gentle-float',
        'smooth-slide',
        'hollywood-dramatic',
        'epic-reveal',
        'dramatic-focus',
        'aerial-sweep',
        'directors-cut',
        'epic-pan',
        'dolly-zoom',
      ],
      animationIndex: 0,
      animationStyle: 'dolly-zoom',
      aspectRatio: '16:9',
      animationTimer: null,
      currentAnimation: 'dolly-zoom',
      noRepeatAnimation: false,
      holdAnimationAtEnd: true,
      synchronizeWithSpeaking: true,
      segmentInProgress: false,

      // For handling failed images
      failedImageUrls: new Set(),

      // Placeholder images
      placeholderImages: [
        "/static/Gemini_Generated_Image_afdde4afdde4afdd.jpeg",
        "/static/Gemini_Generated_Image_arxot0arxot0arxo.jpeg",
        "/static/Gemini_Generated_Image_jmfhwfjmfhwfjmfh.jpeg",
        "/static/Gemini_Generated_Image_ttj4r8ttj4r8ttj4.jpeg",
        "/static/Gemini_Generated_Image_28fjog28fjog28fj.jpeg",
        "/static/Gemini_Generated_Image_z58uvpz58uvpz58u.jpeg",
        "/static/Gemini_Generated_Image_2p68i32p68i32p68.jpeg",
        "/static/Gemini_Generated_Image_lmzdtllmzdtllmzd.jpeg",
        "/static/Gemini_Generated_Image_nvx3l3nvx3l3nvx3.jpeg",
        "/static/Gemini_Generated_Image_hrlvv0hrlvv0hrlv.jpeg",
        "/static/Gemini_Generated_Image_wuxx6lwuxx6lwuxx.jpeg",
        "/static/Gemini_Generated_Image_8a43t08a43t08a43.jpeg",
        "/static/Gemini_Generated_Image_cq7nnqcq7nnqcq7n.jpeg",
        "/static/Gemini_Generated_Image_sekakwsekakwseka.jpeg",
        "/static/Gemini_Generated_Image_zcz3ndzcz3ndzcz3.jpeg",
      ],
      currentPlaceholderImageIndex: Math.floor(Math.random() * 16),
      previousImageState: false,
      isSegmentImageShowing: false,
      lastInteractionTime: Date.now(),

      // Recording specific states
      forceHideAllUI: false,
      isRecordingPaused: false,
      pendingStream: null,
      countdownDuration: 7,
      showCountdown: false
    };
  },
  computed: {
    citationSources() {
      if (!this.isPlaying || !this.activeSegmentRawText) {
        return [];
      }

      return this.extractCitationSources(this.activeSegmentRawText);
    },
    shouldShowIntroMontage() {
      return this.activeSegment === 0 && this.enableIntroMontage && this.montageImages.length > 0;
    },
    // Get current aspect ratio class for CSS
    aspectRatioClass() {
      switch (this.aspectRatio) {
        case '16:9': return 'aspect-16-9';
        case '4:3': return 'aspect-4-3';
        case '1:1': return 'aspect-1-1';
        case '9:16': return 'aspect-9-16';
        default: return 'aspect-16-9';
      }
    },

    // Get the current random placeholder image
    randomPlaceholderImage() {
      return this.placeholderImages[this.currentPlaceholderImageIndex];
    },

    // Filter images from search results
    searchResultImages() {
     return this.filteredImages;
    },

    imageSegmentMapping() {
      const images = this.searchResultImages;
      if (!images || images.length === 0) return [];

      const mapping = [];
      const totalSegments = this.totalSegments || 30;

      for (let segment = 0; segment < totalSegments; segment++) {
        const imageIndex = segment % images.length;
        mapping.push({ segmentIndex: segment, imageIndex });
      }

      return mapping;
    },

    currentImage() {
      const mapping = this.imageSegmentMapping.find(m => m.segmentIndex === this.activeSegment);

      if (mapping && this.searchResultImages.length > mapping.imageIndex) {
        return this.searchResultImages[mapping.imageIndex];
      }

      return this.searchResultImages.length > 0 ? this.searchResultImages[0] : null;
    },

    shouldDisplayImage() {
      if (this.showDocumentsPanel) return false;
      if (!this.currentImage) return false;
      return this.imageSegmentMapping.some(mapping => mapping.segmentIndex === this.activeSegment);
    }
  },
  watch: {
    conversationEnded(newVal) {
      // stop recording if conversation ended
      setTimeout(() => {
        if (newVal && this.isRecording) {
          this.stopRecording();
        }
      }, 1000);
    },
    searchResults: {
      handler(newResults) {
        if (newResults && newResults.length > 0) {
          // Start with unfiltered images immediately for responsiveness
          this.filteredImages = this.getUnfilteredImages();

          // Then load filtered images asynchronously
          this.loadFilteredImagesAsync();
        } else {
          this.filteredImages = [];
        }
      },
      immediate: true // This is important - runs on component creation
    },
    isPlaying(newVal) {
      if (newVal) {
        // Started playing
        this.segmentInProgress = true;
        this.restartAnimations();

        // start the intro montage if applicable
        if (this.activeSegment === 0 && this.enableIntroMontage) {
          this.stopIntroSequence();
          this.prepareMontageForIntro();
          this.startIntroSequence();
        }

        // Always hide controls when playing in fullscreen
        if (this.isFullscreen) {
          this.showControls = false;
          this.forceHideAllUI = true; // Add this line
        } else if (this.recordingMode && this.isRecording) {
          this.showControls = false;
        } else {
          this.showControlsTemporarily();
        }
      } else {
        // Stopped playing
        this.segmentInProgress = false;
        this.forceHideAllUI = false; // Add this line

        // Always show controls when stopped, unless recording
        if (!this.isRecording) {
          this.showControls = true;
        }

        // stop the recording affter a timeout
        setTimeout(() => {
          if (this.isRecording) {
            this.stopRecording();
          }
        }, 1000);

      }
    },

    // Watch for changes in the image display state
    shouldDisplayImage(newValue, oldValue) {
      if (newValue !== oldValue) {
        // Emit event when the visibility changes
        this.$emit('image-visibility-changed', newValue, this.currentImage);
        this.isSegmentImageShowing = newValue;
      }
      this.previousImageState = newValue;
    },

    // Update the activeSegment watch handler
    activeSegment(newSegment, oldSegment) {
      // Check image display state after segment changes
      this.$nextTick(() => {
        if (this.shouldDisplayImage !== this.previousImageState) {
          this.$emit('image-visibility-changed', this.shouldDisplayImage, this.currentImage);
          this.isSegmentImageShowing = this.shouldDisplayImage;
          this.previousImageState = this.shouldDisplayImage;
        }

        // Select a new random placeholder when segment changes
        this.selectRandomPlaceholder();

        // Handle animation style changes
        if (this.animationStyle === 'random' && this.isPlaying) {
          this.changeRandomAnimation();
        }

        // Special handling for intro segment
        if (newSegment === 0 && this.enableIntroMontage) {
          this.prepareMontageForIntro();
          this.startIntroSequence();
        } else {
          this.stopIntroSequence();
        }

        // Restart animations
        this.restartAnimations();
      });
    },

    // Watch for active segment content changes
    activeSegmentContent(newContent, oldContent) {
      if (newContent !== oldContent && newContent !== '') {
        // New segment content
        this.segmentInProgress = true;

        // Handle animation style changes
        if (this.animationStyle === 'random' && this.isPlaying) {
          this.changeRandomAnimation();
        }

        // Restart animations
        if (this.synchronizeWithSpeaking && this.isPlaying) {
          this.restartAnimations();
        }
      } else if (newContent === '' && oldContent !== '') {
        this.segmentInProgress = false;
      }
    },

    // Watch recording mode changes
    recordingMode(newVal) {
      // If entering recording mode, hide controls
      if (newVal && this.isPlaying) {
        this.showControls = false;
      }
    }
  },
  mounted() {
    this.loadSettings();
    this.addEventListeners();
    this.startAnimationCycle();

    // Initial emission
    this.$nextTick(() => {
      this.$emit('image-visibility-changed', this.shouldDisplayImage, this.currentImage);
      this.isSegmentImageShowing = this.shouldDisplayImage;
      this.previousImageState = this.shouldDisplayImage;
    });
    // Check for intro montage on initial load
    if (this.activeSegment === 0 && this.enableIntroMontage) {
      this.prepareMontageForIntro();
      this.startIntroSequence();
    }

    // Load filtered images asynchronously WITHOUT blocking mounted
    this.loadFilteredImagesAsync();
  },
  beforeDestroy() {
    this.removeEventListeners();

    if (this.controlsTimer) {
      clearTimeout(this.controlsTimer);
    }

    if (this.animationTimer) {
      clearInterval(this.animationTimer);
    }
    // Clear intro sequence timers
    if (this.introMontageTimer) {
      clearInterval(this.introMontageTimer);
    }

    if (this.introSequenceTimer) {
      clearTimeout(this.introSequenceTimer);
    }
  },
  methods: {
    // Font Settings Methods - Add these to your Vue component methods

// Save font settings to localStorage
saveFontSettings() {
  // Save font settings to localStorage
  localStorage.setItem(`font_settings_${this.messageId}`, JSON.stringify(this.fontSettings));

  // Determine current preset
  this.currentFontPreset = this.getCurrentFontPreset();
},

// Load font settings from localStorage
loadFontSettings() {
  const savedFontSettings = localStorage.getItem(`font_settings_${this.messageId}`);
  if (savedFontSettings) {
    try {
      const settings = JSON.parse(savedFontSettings);
      this.fontSettings = { ...this.fontSettings, ...settings };
      this.currentFontPreset = this.getCurrentFontPreset();
    } catch (e) {
      console.error('Error loading font settings:', e);
    }
  }
},

// Detect which preset matches current settings
getCurrentFontPreset() {
  // Check if current settings match any preset
  for (const preset of this.fontPresets) {
    const match = Object.keys(preset.settings).every(key => {
      return String(this.fontSettings[key]) === String(preset.settings[key]);
    });
    if (match) {
      return preset.name;
    }
  }
  return 'custom';
},

// Apply a specific font preset
applyFontPreset(presetName) {
  const preset = this.fontPresets.find(p => p.name === presetName);
  if (preset) {
    this.fontSettings = { ...preset.settings };
    this.currentFontPreset = presetName;
    this.saveFontSettings();
  }
},

// Reset font settings to defaults
resetFontSettings() {
  this.fontSettings = {
    fontFamily: 'system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
    fontSize: 18,
    fontWeight: 400,
    lineHeight: 1.5,
    letterSpacing: 0.3,
    textColor: 'white',
    textShadow: '0 1px 2px rgba(0, 0, 0, 0.3)'
  };
  this.currentFontPreset = 'default';
  this.saveFontSettings();
},
    // DON'T make mounted() async - instead use this pattern:
    // Separate async method for loading filtered images
    async loadFilteredImagesAsync() {
      try {
        if (this.searchResults && this.searchResults.length > 0) {
          this.filteredImages = await this.getFilteredSearchResultImages();

          // Emit update after images are loaded
          this.$nextTick(() => {
            this.$emit('image-visibility-changed', this.shouldDisplayImage, this.currentImage);
          });
        }
      } catch (error) {
        console.error('Error loading filtered images:', error);
        // Fallback to unfiltered images
        this.filteredImages = this.getUnfilteredImages();
      }
    },

    // Fallback method for unfiltered images
    getUnfilteredImages() {
      return this.searchResults.filter(result => {
        if (!result.url) return false;

        const url = result.url.toLowerCase();
        if (url.includes('facebook.com') || url.includes('fbcdn.net') ||
            url.includes('instagram.com')) {
          return false;
        }

        if (result.isImage) return true;

        const imageExtensions = [
          '.jpg', '.jpeg', '.png', '.gif', '.webp', '.svg', '.bmp',
          '.ico', '.tiff', '.tif', '.img', '.heic', '.heif', '.raw', '.avif'
        ];

        const hasImageExtension = imageExtensions.some(ext => url.includes(ext));
        if (hasImageExtension) return true;

        const imagePatterns = [
          'image', 'img', 'picture', 'photo', 'thumbnail', 'thumb',
          '/images/', '/pics/', '/photos/', '/avatars/', '/covers/',
          'media.licdn.com/dms/image', 'profile-images', 'user-images',
          'twimg.com', 'unsplash.com', 'pexels.com', 'imgur.com',
          'pinimg.com', 'cloudfront.net', 'cloudinary.com',
          'googleusercontent.com', 'gravatar.com'
        ];

        return imagePatterns.some(pattern => url.includes(pattern));
      });
    },
    async filterHighQualityImages(images) {
      const qualityPromises = images.map(async (image) => {
        try {
          const quality = await this.assessImageQuality(image);
          return { ...image, qualityScore: quality.score, qualityReasons: quality.reasons };
        } catch (error) {
          console.warn('Error assessing image quality:', error);
          return { ...image, qualityScore: 0, qualityReasons: ['assessment_failed'] };
        }
      });

      const imagesWithQuality = await Promise.all(qualityPromises);

      // Filter out low quality images (score below the minimum threshold)
      const filteredImagesWithQuality = imagesWithQuality.filter(img => img.qualityScore >= this.imageQualityMin);
      return imagesWithQuality.filter(img => img.qualityScore >= this.imageQualityMin);
  },

    // Comprehensive image quality assessment
    async assessImageQuality(image) {
      let score = 10;
      const reasons = [];

      // 1. URL-based quality indicators
      const urlQuality = this.assessUrlQuality(image.url);
      score += urlQuality.scoreAdjustment;
      reasons.push(...urlQuality.reasons);

      // 2. Load image and check dimensions/file size
      try {
        const imageData = await this.loadImageData(image.url || image.image);

        // Check dimensions
        if (imageData.width < 400 || imageData.height < 300) {
          score -= 4;
          reasons.push('too_small');
        } else if (imageData.width < 800 || imageData.height < 600) {
          score -= 2;
          reasons.push('small_resolution');
        }

        // Check aspect ratio extremes
        const aspectRatio = imageData.width / imageData.height;
        if (aspectRatio > 3 || aspectRatio < 0.3) {
          score -= 3;
          reasons.push('extreme_aspect_ratio');
        }

        // 3. Detect blur using canvas analysis (optional - more intensive)
        if (this.enableBlurDetection) {
          const blurScore = await this.detectImageBlur(imageData.element);
          if (blurScore > 0.7) { // High blur threshold
            score -= 5;
            reasons.push('blurry');
          } else if (blurScore > 0.4) {
            score -= 2;
            reasons.push('slightly_blurry');
          }
        }

      } catch (error) {
        score -= 3;
        reasons.push('load_failed');
      }

      return {
        score: Math.max(0, Math.min(10, score)),
        reasons: reasons.filter(Boolean)
      };
    },

    // Quick URL-based quality assessment
    assessUrlQuality(url) {
      let scoreAdjustment = 0;
      const reasons = [];

      if (!url) return { scoreAdjustment: -5, reasons: ['no_url'] };

      const urlLower = url.toLowerCase();

      // Positive indicators
      if (urlLower.includes('1920x1080') || urlLower.includes('1280x720') ||
          urlLower.includes('hd') || urlLower.includes('large')) {
        scoreAdjustment += 2;
        reasons.push('hd_indicators');
      }

      // Negative indicators
      if (urlLower.includes('thumbnail') || urlLower.includes('thumb') ||
          urlLower.includes('small') || urlLower.includes('icon')) {
        scoreAdjustment -= 3;
        reasons.push('thumbnail_indicators');
      }

      if (urlLower.includes('avatar') || urlLower.includes('profile')) {
        scoreAdjustment -= 2;
        reasons.push('profile_image');
      }

      // File size indicators in URL
      if (urlLower.match(/\b(s|xs|sm)\b/) || urlLower.includes('150x150') ||
          urlLower.includes('200x200')) {
        scoreAdjustment -= 2;
        reasons.push('small_size_indicators');
      }

      // High quality sources
      const highQualitySources = [
        'unsplash.com', 'pexels.com', 'pixabay.com', 'shutterstock.com',
        'gettyimages.com', 'adobe.com', 'flickr.com'
      ];

      if (highQualitySources.some(source => urlLower.includes(source))) {
        scoreAdjustment += 1;
        reasons.push('quality_source');
      }

      return { scoreAdjustment, reasons };
    },

    // Load image and get dimensions
    loadImageData(url) {
      return new Promise((resolve, reject) => {
        const img = new Image();
        img.crossOrigin = 'anonymous'; // For CORS

        img.onload = () => {
          resolve({
            width: img.naturalWidth,
            height: img.naturalHeight,
            element: img
          });
        };

        img.onerror = () => {
          reject(new Error('Failed to load image'));
        };

        // Timeout after 10 seconds
        setTimeout(() => {
          reject(new Error('Image load timeout'));
        }, 10000);

        img.src = url;
      });
    },

    // Advanced blur detection using canvas (optional - more resource intensive)
    async detectImageBlur(imgElement) {
      try {
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');

        // Scale down for performance
        const maxSize = 200;
        const scale = Math.min(maxSize / imgElement.width, maxSize / imgElement.height);
        canvas.width = imgElement.width * scale;
        canvas.height = imgElement.height * scale;

        ctx.drawImage(imgElement, 0, 0, canvas.width, canvas.height);

        // Get image data
        const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
        const data = imageData.data;

        // Simple edge detection for blur
        let edgeSum = 0;
        let edgeCount = 0;

        for (let y = 1; y < canvas.height - 1; y++) {
          for (let x = 1; x < canvas.width - 1; x++) {
            const i = (y * canvas.width + x) * 4;

            // Calculate gradient using Sobel operator
            const gx = (
              -1 * data[i - 4 - canvas.width * 4] +
              1 * data[i + 4 - canvas.width * 4] +
              -2 * data[i - 4] +
              2 * data[i + 4] +
              -1 * data[i - 4 + canvas.width * 4] +
              1 * data[i + 4 + canvas.width * 4]
            );

            const gy = (
              -1 * data[i - 4 - canvas.width * 4] +
              -2 * data[i - canvas.width * 4] +
              -1 * data[i + 4 - canvas.width * 4] +
              1 * data[i - 4 + canvas.width * 4] +
              2 * data[i + canvas.width * 4] +
              1 * data[i + 4 + canvas.width * 4]
            );

            const magnitude = Math.sqrt(gx * gx + gy * gy);
            edgeSum += magnitude;
            edgeCount++;
          }
        }

        const avgEdgeStrength = edgeSum / edgeCount;

        // Normalize and invert (higher score = more blur)
        return Math.max(0, Math.min(1, (50 - avgEdgeStrength) / 50));

      } catch (error) {
        console.warn('Blur detection failed:', error);
        return 0.5; // Unknown blur level
      }
    },

    // Update your existing searchResultImages computed property
    async getFilteredSearchResultImages() {
      const baseImages = this.searchResults.filter(result => {
        if (!result.url) return false;

        const url = result.url.toLowerCase();

        // Existing filters
        if (url.includes('facebook.com') || url.includes('fbcdn.net') ||
            url.includes('instagram.com')) {
          return false;
        }

        if (result.isImage) return true;

        const imageExtensions = [
          '.jpg', '.jpeg', '.png', '.gif', '.webp', '.svg', '.bmp',
          '.ico', '.tiff', '.tif', '.img', '.heic', '.heif', '.raw', '.avif'
        ];

        const hasImageExtension = imageExtensions.some(ext => url.includes(ext));
        if (hasImageExtension) return true;

        const imagePatterns = [
          'image', 'img', 'picture', 'photo', 'thumbnail', 'thumb',
          '/images/', '/pics/', '/photos/', '/avatars/', '/covers/',
          'media.licdn.com/dms/image', 'profile-images', 'user-images',
          'twimg.com', 'unsplash.com', 'pexels.com', 'imgur.com',
          'pinimg.com', 'cloudfront.net', 'cloudinary.com',
          'googleusercontent.com', 'gravatar.com'
        ];

        return imagePatterns.some(pattern => url.includes(pattern));
      });

      // Apply quality filtering
      return await this.filterHighQualityImages(baseImages);
    },
    extractCitationSources(segmentContent) {
      if (!segmentContent) return [];

      // Create a temporary div to parse the HTML content
      const tempDiv = document.createElement('div');
      tempDiv.innerHTML = segmentContent;

      // Find all citation links in the segment
      const citationLinks = tempDiv.querySelectorAll('.citation-link');
      const sources = new Map(); // Use Map to deduplicate by URL

      citationLinks.forEach(citation => {
        try {
          // Extract URL from citation data
          const citationId = citation.dataset.citation;
          const uniqueId = `${this.messageId}-${citationId}`;

          // Check if we have the URL stored (from citation renderer logic)
          let url = this.citationUrls?.get(uniqueId);

          if (!url) {
            // Try to extract from source text or metadata
            url = this.extractUrlFromCitation(citation);
          }

          if (url && this.isValidUrl(url)) {
            const domain = this.formatUrl(url);

            // Only add if not already in sources (deduplicate)
            if (!sources.has(url)) {
              sources.set(url, {
                url: url,
                domain: domain
              });
            }
          }
        } catch (error) {
          console.warn('Error extracting citation source:', error);
        }
      });

      return Array.from(sources.values());
    },

    extractUrlFromCitation(citation) {
      try {
        // Try to get URL from source text
        const sourceText = citation.dataset.sourceText || '';

        // Look for URL patterns in source text
        const urlMatches = [
          ...sourceText.matchAll(/\[URL:\s*(https?:\/\/[^\]]+)\]/g),
          ...sourceText.matchAll(/\[Source:\s*(https?:\/\/[^\]]+)\]/g),
          ...sourceText.matchAll(/(https?:\/\/[^\s)}\]]+)/g)
        ];

        if (urlMatches.length > 0) {
          return urlMatches[0][1];
        }

        // Try to get from metadata with better error handling
        const metadata = citation.dataset.metadata;
        if (metadata) {
          try {
            // Sanitize the JSON string before parsing
            const sanitizedMetadata = this.sanitizeJsonString(metadata);
            const parsedMetadata = JSON.parse(sanitizedMetadata);
            if (parsedMetadata.url) {
              return parsedMetadata.url;
            }
          } catch (e) {
            console.warn('Error parsing metadata:', e, 'Raw metadata:', metadata);
          }
        }

        return null;
      } catch (error) {
        console.warn('Error extracting URL from citation:', error);
        return null;
      }
    },

    // Add the sanitizeJsonString method from CitationRenderer
    sanitizeJsonString(jsonString) {
      try {
        // Remove any HTML tags
        const withoutTags = jsonString.replace(/<[^>]*>/g, '');

        // Decode HTML entities
        const decoded = this.decodeHtmlEntities(withoutTags);

        // Fix any double-encoded quotes
        const fixedQuotes = decoded.replace(/&quot;/g, '"')
                                  .replace(/\\"/g, '"')
                                  .replace(/""/, '"');

        // Ensure it's valid JSON structure
        if (!fixedQuotes.startsWith('{')) {
          return '{}';
        }

        return fixedQuotes;
      } catch (error) {
        console.warn('Error sanitizing JSON string:', error);
        return '{}';
      }
    },

    // Add the decodeHtmlEntities method from CitationRenderer
    decodeHtmlEntities(str) {
      const txt = document.createElement('textarea');
      txt.innerHTML = str;
      return txt.value;
    },

    isValidUrl(string) {
      try {
        new URL(string);
        return true;
      } catch (_) {
        return false;
      }
    },
    getMontageImageStyle(index) {
      const isActive = index === this.montageImageIndex;

      return {
        opacity: isActive ? 1 : 0,
        zIndex: isActive ? 10 : 1
      };
    },
    // Prepare montage images for intro sequence
    prepareMontageForIntro() {
      const images = this.searchResultImages;
      if (!images || images.length === 0) return;

      // For montage, select diverse and visually striking images
      // Take 5-10 images spread throughout the collection
      const montageCount = Math.min(15, images.length);
      this.montageImages = [];

      if (images.length <= montageCount) {
        this.montageImages = [...images];
        return;
      }

      // Get images spread throughout the collection
      const step = Math.floor(images.length / montageCount);
      for (let i = 0; i < montageCount; i++) {
        const index = i * step;
        if (index < images.length) {
          this.montageImages.push(images[index]);
        }
      }
    },

    // Start the intro montage sequence
    startIntroSequence() {
      if (!this.enableIntroMontage || !this.montageImages.length) return;

      this.isIntroSequencePlaying = true;
      this.montageImageIndex = 0;

      // Clear any existing timers
      if (this.introMontageTimer) {
        clearInterval(this.introMontageTimer);
      }

      // Start rapid image sequence
      const introSpeed = 2000; // Default to 1 second per image, adjust as needed
      this.introMontageTimer = setInterval(() => {
        this.montageImageIndex = (this.montageImageIndex + 1) % this.montageImages.length;

        // After showing all images, end the montage
        if (this.montageImageIndex === 0) {
          // For second cycle, slow down the pace
          clearInterval(this.introMontageTimer);
          this.introMontageTimer = setInterval(() => {
            this.montageImageIndex = (this.montageImageIndex + 1) % this.montageImages.length;

            // After second complete cycle, end montage
            if (this.montageImageIndex === 0) {
              this.stopIntroSequence();
            }
          }, introSpeed * 1.5); // Slower for second cycle
        }
      }, introSpeed);

      // Set a timeout to end the intro sequence after max duration
      this.introSequenceTimer = setTimeout(() => {
        this.stopIntroSequence();
      }, Math.min(15000, this.montageImages.length * introSpeed * 2.5)); // Max 15 seconds
    },

    // Stop the intro montage sequence
    stopIntroSequence() {
      this.isIntroSequencePlaying = false;

      if (this.introMontageTimer) {
        clearInterval(this.introMontageTimer);
        this.introMontageTimer = null;
      }

      if (this.introSequenceTimer) {
        clearTimeout(this.introSequenceTimer);
        this.introSequenceTimer = null;
      }
    },
    // ---- Event Handling ----
    addEventListeners() {
      // Add fullscreen change event listener
      document.addEventListener('fullscreenchange', this.handleFullscreenChange);
      document.addEventListener('webkitfullscreenchange', this.handleFullscreenChange);

      // Add keyboard event listeners
      window.addEventListener('keydown', this.handleKeyDown);

      // Mouse movement tracking
      if (this.$el) {
        this.$el.addEventListener('mousemove', this.handleMouseMove);
      }
    },

    removeEventListeners() {
      document.removeEventListener('fullscreenchange', this.handleFullscreenChange);
      document.removeEventListener('webkitfullscreenchange', this.handleFullscreenChange);
      window.removeEventListener('keydown', this.handleKeyDown);

      if (this.$el) {
        this.$el.removeEventListener('mousemove', this.handleMouseMove);
      }
    },

    handleKeyDown(e) {
      // Only handle if this component is focused
      if (document.activeElement !== this.$el && !this.isFullscreen) return;

      // Handle recording-specific keys first
      if (this.isRecording) {
        this.handleRecordingKeyDown(e);
        return;
      }

      switch (e.key) {
        case ' ': // Space - Play/Pause
          e.preventDefault();
          this.togglePlay();
          break;
        case 'f': // F - Fullscreen
        case 'F':
          e.preventDefault();
          this.toggleFullscreen();
          break;
        case 'ArrowLeft': // Left arrow - Previous
          e.preventDefault();
          this.skipPrevious();
          break;
        case 'ArrowRight': // Right arrow - Next
          e.preventDefault();
          this.skipNext();
          break;
        case '?': // ? - Show keyboard shortcuts
          e.preventDefault();
          this.showKeyboardShortcuts = !this.showKeyboardShortcuts;
          break;
        case 'r': // R - Toggle recording mode
        case 'R':
          e.preventDefault();
          this.recordingMode = !this.recordingMode;
          this.saveSettings();
          break;
      }
    },

    handleEscKey() {
      if (this.isRecording) {
        this.stopRecording();
      } else if (this.isFullscreen) {
        this.exitFullscreen();
      }
    },

    handleMouseMove() {
      // Update last interaction time
      this.lastInteractionTime = Date.now();

      // Only show controls on mouse move if not in fullscreen playing mode
      if (!(this.isFullscreen && this.isPlaying)) {
        if (!this.recordingMode || !this.isPlaying || !this.isRecording) {
          this.showControlsTemporarily();
        }
      }
    },

    // ---- UI Control Methods ----
    togglePlay() {
      this.$emit('toggle-play');
    },

    playOnly() {
      // Just play without recording
      this.$emit('toggle-play');
    },

    recordAndPlay() {
      // Start recording and then play
      if (!this.isRecording) {
        this.forceHideAllUI = true;
        this.startRecording();
      }
    },

    skipPrevious() {
      this.$emit('skip-previous');
    },

    skipNext() {
      this.$emit('skip-next');
    },

    toggleSettings() {
      this.showSettingsPanel = !this.showSettingsPanel;
    },

    // Show controls temporarily
    showControlsTemporarily() {
       // If in fullscreen and playing, never show controls
      if (this.isFullscreen && this.isPlaying) {
        this.showControls = false;
        return; // Exit early
      }

      // Clear existing timer
      if (this.controlsTimer) {
        clearTimeout(this.controlsTimer);
      }

      // Show controls
      this.showControls = true;

      // Hide after delay if playing in recording mode and not recording
      if (this.isPlaying && this.recordingMode && !this.isRecording) {
        this.controlsTimer = setTimeout(() => {
          this.showControls = false;
        }, 2500);
      }
    },

    // Start recording with countdown
    async startRecordingWithCountdown() {
      try {
        // First request permissions
        if (!await this.initializeRecording()) {
          return;
        }

        this.isPreparingRecording = true;
        this.recordingError = null;

        // Get display media (tab capture) with enhanced audio
        const displayStream = await navigator.mediaDevices.getDisplayMedia({
          video: this.recordingQuality.video,
          audio: {
            echoCancellation: false,
            noiseSuppression: false,
            autoGainControl: false,
            sampleRate: { ideal: 48000 },
            channelCount: { ideal: 2 }
          },
          preferCurrentTab: true,
        });

        // Check if user selected current tab
        const videoTrack = displayStream.getVideoTracks()[0];
        const settings = videoTrack.getSettings();

        // Only proceed if a browser tab was selected
        if (settings.displaySurface !== 'browser') {
          this.recordingError = 'Please select a browser tab to record';
          displayStream.getTracks().forEach(track => track.stop());
          this.isPreparingRecording = false;
          return;
        }

        // Store the stream for later use
        this.pendingStream = displayStream;

        // Handle stream ended (user stopped sharing)
        displayStream.getVideoTracks()[0].onended = () => {
          this.cancelCountdown();
        };

        // Enter fullscreen first
        if (!this.isFullscreen) {
          await this.enterFullscreen();
        }

        // Wait a moment for fullscreen to settle
        await new Promise(resolve => setTimeout(resolve, 300));

        // Show countdown
        this.showCountdown = true;

        // Start countdown
        this.$nextTick(() => {
          const countdownRef = this.shouldDisplayImage ? 'countdown' : 'emptyStateCountdown';
          if (this.$refs[countdownRef]) {
            this.$refs[countdownRef].start();
          }
        });

      } catch (error) {
        console.error('Error preparing recording:', error);
        this.recordingError = error.message || 'Failed to start recording';
        this.isPreparingRecording = false;
        this.cleanup();
      }
    },

    // Handle countdown complete
    async onCountdownComplete() {
      this.showCountdown = false;

      // Give time for countdown to disappear and DOM to update
      await this.$nextTick();
      await new Promise(resolve => setTimeout(resolve, 300));

      if (!this.pendingStream) {
        this.recordingError = 'Recording stream not available';
        this.isPreparingRecording = false;
        return;
      }

      try {
        // Set up MediaRecorder with the stream
        this.recordingStream = this.pendingStream;
        this.pendingStream = null;

        this.mediaRecorder = new MediaRecorder(this.recordingStream, this.recordingOptions);
        this.recordedChunks = [];

        // Handle data available
        this.mediaRecorder.ondataavailable = (event) => {
          if (event.data.size > 0) {
            this.recordedChunks.push(event.data);
          }
        };

        // Handle recording stopped
        this.mediaRecorder.onstop = () => {
          this.saveRecording();
          this.cleanup();
        };

        // Handle errors
        this.mediaRecorder.onerror = (event) => {
          console.error('MediaRecorder error:', event);
          this.recordingError = 'Recording error occurred';
          this.stopRecording();
        };

        // Start recording
        this.mediaRecorder.start(1000);
        this.isRecording = true;
        this.forceHideAllUI = true;
        this.recordingStartTime = Date.now();
        this.startRecordingTimer();
        this.isPreparingRecording = false;

        // Start playback
        if (!this.isPlaying) {
          this.togglePlay();
        }

        // Hide all UI controls for clean recording
        this.showControls = false;
        this.forceHideAllUI = true;

      } catch (error) {
        console.error('Error starting recording:', error);
        this.recordingError = error.message || 'Failed to start recording';
        this.isPreparingRecording = false;
        this.cleanup();
      }
    },

    // Handle countdown cancelled
    onCountdownCancelled() {
      this.showCountdown = false;
      this.isPreparingRecording = false;

      // Stop the pending stream
      if (this.pendingStream) {
        this.pendingStream.getTracks().forEach(track => track.stop());
        this.pendingStream = null;
      }

      // Exit fullscreen if we entered it
      if (this.isFullscreen) {
        this.exitFullscreen();
      }

      // Show controls again
      this.showControls = true;
    },

    // Cancel countdown (from external trigger)
    cancelCountdown() {
      const countdownRef = this.shouldDisplayImage ? 'countdown' : 'emptyStateCountdown';
      if (this.$refs[countdownRef]) {
        this.$refs[countdownRef].cancelCountdown();
      }
    },

    // Recording pause toggle
    toggleRecordingPause() {
      if (this.mediaRecorder) {
        if (this.mediaRecorder.state === 'recording') {
          this.mediaRecorder.pause();
          this.isRecordingPaused = true;
        } else if (this.mediaRecorder.state === 'paused') {
          this.mediaRecorder.resume();
          this.isRecordingPaused = false;
        }
      }
    },

    // ---- Animation Methods ----
    changeRandomAnimation() {
      let newIndex;
      do {
        newIndex = Math.floor(Math.random() * this.animations.length);
      } while (newIndex === this.animationIndex && this.animations.length > 1);

      this.animationIndex = newIndex;
      this.currentAnimation = this.animations[this.animationIndex];

      const animContainer = this.$el.querySelector('.image-animation-container');
      if (animContainer) {
        animContainer.classList.add('animation-transition');

        this.animations.forEach(anim => {
          animContainer.classList.remove(anim);
        });

        void animContainer.offsetWidth;

        animContainer.classList.add(this.currentAnimation);

        setTimeout(() => {
          animContainer.classList.remove('animation-transition');
        }, 1000);
      }
    },

    restartAnimations() {
      const animContainer = this.$el.querySelector('.image-animation-container');
      if (animContainer) {
        if (this.segmentInProgress) {
          animContainer.classList.add('animation-transition');
        }

        this.animations.forEach(anim => {
          animContainer.classList.remove(anim);
        });

        void animContainer.offsetWidth;

        animContainer.classList.add(this.currentAnimation);

        if (this.holdAnimationAtEnd || this.synchronizeWithSpeaking) {
          // Estimate reading time based on content length
          const wordCount = this.activeSegmentContent.split(/\s+/).length;
          const estimatedSeconds = Math.max(10, Math.min(60, (wordCount / 90) * 60));

          animContainer.style.animationDuration = `${estimatedSeconds}s`;
          this.animationDuration = estimatedSeconds;
        } else {
          animContainer.style.animationDuration = '';
        }

        setTimeout(() => {
          animContainer.classList.remove('animation-transition');
        }, 1000);
      }
    },

    startAnimationCycle() {
      if (this.animationTimer) {
        clearInterval(this.animationTimer);
        this.animationTimer = null;
      }

      if (this.animationStyle === 'random' && !this.holdAnimationAtEnd && !this.synchronizeWithSpeaking) {
        this.currentAnimation = this.animations[this.animationIndex];

        if (!this.noRepeatAnimation) {
          this.animationTimer = setInterval(() => {
            if (!this.segmentInProgress || !this.synchronizeWithSpeaking) {
              this.changeRandomAnimation();
            }
          }, 15000);
        }
      } else {
        const animMap = {
          'zoom-pan': 'zoom-pan-effect',
          'ken-burns': 'ken-burns-effect',
          'fade-scale': 'fade-and-scale',
          'gentle-float': 'gentle-float',
          'smooth-slide': 'smooth-slide',
          'hollywood-dramatic': 'hollywood-dramatic',
          'epic-reveal': 'epic-reveal',
          'dramatic-focus': 'dramatic-focus',
          'aerial-sweep': 'aerial-sweep',
          'directors-cut': 'directors-cut',
          'epic-pan': 'epic-pan',
          'dolly-zoom': 'dolly-zoom',
        };
        this.currentAnimation = animMap[this.animationStyle] || 'zoom-pan-effect';
      }

      this.$nextTick(() => {
        this.restartAnimations();
      });
    },

    // ---- Settings Methods ----
    setAnimationStyle(style) {
      this.animationStyle = style;
      this.startAnimationCycle();
      this.saveSettings();

      if (this.isPlaying) {
        this.restartAnimations();
      }
    },

    setAspectRatio(ratio) {
      this.aspectRatio = ratio;
      this.saveSettings();
    },

    saveSettings() {
      localStorage.setItem(`image_panel_settings_${this.messageId}`, JSON.stringify({
        aspectRatio: this.aspectRatio,
        animationStyle: this.animationStyle,
        noRepeatAnimation: this.noRepeatAnimation,
        holdAnimationAtEnd: this.holdAnimationAtEnd,
        synchronizeWithSpeaking: this.synchronizeWithSpeaking,
        recordingMode: this.recordingMode,
        enableIntroMontage: this.enableIntroMontage
      }));
    },

    loadSettings() {
      const savedSettings = localStorage.getItem(`image_panel_settings_${this.messageId}`);
      if (savedSettings) {
        try {
          const settings = JSON.parse(savedSettings);
          if (settings.aspectRatio) {
            this.aspectRatio = settings.aspectRatio;
          }
          if (settings.animationStyle) {
            this.animationStyle = settings.animationStyle;
          }
          if (settings.noRepeatAnimation !== undefined) {
            this.noRepeatAnimation = settings.noRepeatAnimation;
          }
          if (settings.holdAnimationAtEnd !== undefined) {
            this.holdAnimationAtEnd = settings.holdAnimationAtEnd;
          }
          if (settings.synchronizeWithSpeaking !== undefined) {
            this.synchronizeWithSpeaking = settings.synchronizeWithSpeaking;
          }
          if (settings.recordingMode !== undefined) {
            this.recordingMode = settings.recordingMode;
          }
          if (settings.enableIntroMontage !== undefined) {
            this.enableIntroMontage = settings.enableIntroMontage;
          }
        } catch (e) {
          console.error('Error loading saved settings:', e);
        }
      }
    },

    // URL formatting
    formatUrl(url) {
      if (!url) return '';

      try {
        const urlObj = new URL(url);
        return urlObj.hostname.replace(/^www\./, '');
      } catch (e) {
        return url.length > 30 ? url.substring(0, 30) + '...' : url;
      }
    },

    // Select a random placeholder image
    selectRandomPlaceholder() {
      this.currentPlaceholderImageIndex = Math.floor(Math.random() * this.placeholderImages.length);
    },

    // Handle image errors
    handleImageError(event) {
      console.warn('Image load error:', event.target.alt);

      const failedUrl = this.currentImage.url || this.currentImage.image;
      this.failedImageUrls.add(failedUrl);

      const availableImages = this.searchResultImages.filter(img =>
        !this.failedImageUrls.has(img.url || img.image)
      );

      if (availableImages.length > 0) {
        const newImage = availableImages[0];

        event.target.src = newImage.image || newImage.thumbnail;
        event.target.alt = newImage.title;

        this.$emit('image-changed', newImage);
      } else {
        const container = event.target.parentNode;

        this.selectRandomPlaceholder();
        const randomPlaceholder = this.randomPlaceholderImage;

        const fallbackImg = document.createElement('img');
        fallbackImg.src = randomPlaceholder;
        fallbackImg.alt = "Fallback image";
        fallbackImg.className = "fallback-image document-image";

        fallbackImg.style.maxHeight = "500px";
        fallbackImg.style.width = "100%";
        fallbackImg.style.objectFit = "cover";

        event.target.style.display = 'none';

        container.appendChild(fallbackImg);

        const errorMsg = document.createElement('div');
        errorMsg.className = 'image-error-message';
        errorMsg.textContent = 'Image could not be loaded, showing a placeholder image';
        container.appendChild(errorMsg);
      }
    },

    // Check if an image is shown for a specific segment
    isImageShownForSegment(segmentIndex) {
      if (this.showCitationPanel || this.showDocumentsPanel) return false;
      if (!this.searchResultImages.length) return false;

      return this.imageSegmentMapping.some(mapping => mapping.segmentIndex === segmentIndex);
    },

    // Open image URL in new tab
    openImageUrl(url) {
      if (url) {
        window.open(url, '_blank', 'noopener,noreferrer');
      }
    },

    toggleFullscreen() {
      if (!this.isFullscreen) {
        this.enterFullscreen();
      } else {
        this.exitFullscreen();
      }

      // If in fullscreen and playing, hide controls
      if (this.isFullscreen && this.isPlaying) {
        this.showControls = false;
        this.forceHideAllUI = true;
      } else {
        // Otherwise show controls temporarily
        this.showControlsTemporarily();
      }
    },

    enterFullscreen() {
      const element = this.$el;

      try {
        if (element.requestFullscreen) {
          element.requestFullscreen();
        } else if (element.webkitRequestFullscreen) {
          element.webkitRequestFullscreen();
        } else if (element.msRequestFullscreen) {
          element.msRequestFullscreen();
        }

        this.isFullscreen = true;
        document.body.classList.add('has-fullscreen-element');

        // Lock orientation to landscape if possible
        if (screen.orientation && screen.orientation.lock) {
          screen.orientation.lock('landscape').catch(e => {
            console.log('Orientation lock not supported');
          });
        }

        // Add event listeners for resizing
        window.addEventListener('resize', this.handleFullscreenResize);
        window.addEventListener('orientationchange', this.handleFullscreenResize);

        // Force immediate resize handling
        this.handleFullscreenResize();
      } catch (e) {
        console.error('Fullscreen API error:', e);
        this.isFullscreen = true;
        document.body.classList.add('has-fullscreen-element');
      }
    },

    exitFullscreen() {
      try {
        if (document.exitFullscreen) {
          document.exitFullscreen();
        } else if (document.webkitExitFullscreen) {
          document.webkitExitFullscreen();
        } else if (document.msExitFullscreen) {
          document.msExitFullscreen();
        }

        // Unlock orientation
        if (screen.orientation && screen.orientation.unlock) {
          screen.orientation.unlock();
        }

        // Remove event listeners
        window.removeEventListener('resize', this.handleFullscreenResize);
        window.removeEventListener('orientationchange', this.handleFullscreenResize);

        document.body.classList.remove('has-fullscreen-element');

        // Clean up styles
        this.$el.style.transform = '';
        this.$el.style.position = '';
        this.$el.style.top = '';
        this.$el.style.marginTop = '';

        // Emit event
        this.$emit('fullscreen-exited');
      } catch (e) {
        console.error('Exit fullscreen error:', e);
      }

      this.isFullscreen = false;

      // IMPORTANT: Ensure controls are visible after exiting fullscreen
      this.$nextTick(() => {
        if (!this.isRecording) {
          this.showControls = true;
          this.forceHideAllUI = false; // Make sure this is reset
        }
      });
    },

    handleFullscreenResize() {
      if (this.isFullscreen) {
        this.$nextTick(() => {
          const element = this.$el;
          if (element) {
            // Force reflow for mobile browsers
            element.style.display = 'none';
            const forceRepaint = element.offsetHeight;
            element.style.display = '';

            setTimeout(() => {
              // Force hardware acceleration
              element.style.transform = 'translateZ(0)';

              // Ensure content fills the screen
              const viewportHeight = window.innerHeight;
              if (element.offsetHeight < viewportHeight) {
                element.style.height = `${viewportHeight}px`;
              }
            }, 50);
          }
        });
      }
    },

    handleFullscreenChange() {
      const isInFullscreen = !!(document.fullscreenElement ||
                                document.webkitFullscreenElement ||
                                document.msFullscreenElement);

      this.isFullscreen = isInFullscreen;

      if (isInFullscreen) {
        document.body.classList.add('has-fullscreen-element');
      } else {
        document.body.classList.remove('has-fullscreen-element');
      }
    }
  }
};
</script>

<style scoped>
/* Recording toast notification - outside the recording area */
.recording-toast {
  position: fixed;
  bottom: 20px;
  left: 50%;
  transform: translateX(-50%);
  background: rgba(0, 0, 0, 0.9);
  color: white;
  padding: 12px 24px;
  border-radius: 24px;
  font-size: 14px;
  display: flex;
  align-items: center;
  gap: 10px;
  z-index: 10001;
  backdrop-filter: blur(8px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
}

.recording-toast-content {
  display: flex;
  align-items: center;
  gap: 10px;
}

.recording-dot {
  width: 8px;
  height: 8px;
  background: #dc2626;
  border-radius: 50%;
  animation: recording-pulse 1.5s ease-in-out infinite;
}

@keyframes recording-pulse {
  0%, 100% {
    opacity: 1;
    transform: scale(1);
  }
  50% {
    opacity: 0.6;
    transform: scale(1.3);
  }
}

.recording-toast kbd {
  background: rgba(255, 255, 255, 0.2);
  border: 1px solid rgba(255, 255, 255, 0.3);
  border-radius: 4px;
  padding: 2px 6px;
  font-family: monospace;
  font-size: 13px;
  margin: 0 3px;
  display: inline-block;
  line-height: 1;
  vertical-align: middle;
}

/* Recording error notification */
.recording-error-notification {
  position: fixed;
  top: 20px;
  left: 50%;
  transform: translateX(-50%);
  background: rgba(220, 38, 38, 0.9);
  color: white;
  padding: 12px 24px;
  border-radius: 8px;
  font-size: 14px;
  display: flex;
  align-items: center;
  gap: 10px;
  z-index: 10001;
  backdrop-filter: blur(8px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
}

.error-close-btn {
  background: none;
  border: none;
  color: white;
  font-size: 20px;
  cursor: pointer;
  padding: 0 4px;
  margin-left: 8px;
  opacity: 0.8;
  transition: opacity 0.2s;
}

.error-close-btn:hover {
  opacity: 1;
}

/* Recording-specific styles */
.segment-image-panel.is-recording .ui-controls-layer,
.empty-state-placeholder.is-recording .ui-controls-layer {
  pointer-events: none;
}

.segment-image-panel.is-recording .ui-controls-layer.recording-hidden,
.empty-state-placeholder.is-recording .ui-controls-layer.recording-hidden {
  opacity: 0;
  visibility: hidden;
}

/* Force hide all UI during recording */
.segment-image-panel.is-recording .ui-controls-layer.hidden,
.empty-state-placeholder.is-recording .ui-controls-layer.hidden {
  opacity: 0 !important;
  visibility: hidden !important;
}

.segment-image-panel.is-recording .ui-controls-layer.hidden *,
.empty-state-placeholder.is-recording .ui-controls-layer.hidden * {
  opacity: 0 !important;
  visibility: hidden !important;
  display: none !important;
}

/* Ensure transcript remains visible during recording */
.segment-image-panel.is-recording .live-transcript,
.empty-state-placeholder.is-recording .live-transcript {
  visibility: visible !important;
  opacity: 1 !important;
}

/* Large record button */
.large-record-button {
  width: 72px;
  height: 72px;
  border-radius: 50%;
  background: linear-gradient(135deg, #dc2626 0%, #b91c1c 100%);
  border: 3px solid rgba(255, 255, 255, 0.2);
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  box-shadow: 0 6px 16px rgba(220, 38, 38, 0.4);
  transition: all 0.3s ease;
  margin: 0 1rem;
  position: relative;
  overflow: hidden;
}

.large-record-button:before {
  content: '';
  position: absolute;
  top: 50%;
  left: 50%;
  width: 200%;
  height: 200%;
  background: radial-gradient(circle, rgba(255,255,255,0.15) 0%, rgba(255,255,255,0) 70%);
  transform: translate(-50%, -50%);
  opacity: 0;
  transition: opacity 0.3s ease;
}

.large-record-button:hover {
  transform: scale(1.1);
  background: linear-gradient(135deg, #ef4444 0%, #dc2626 100%);
  box-shadow: 0 8px 20px rgba(220, 38, 38, 0.5);
}

.large-record-button:hover:before {
  opacity: 1;
}

.large-record-button svg {
  z-index: 1;
  width: 36px;
  height: 36px;
}

.large-record-button svg circle:last-child {
  fill: currentColor;
  animation: pulse-record 2s ease-in-out infinite;
}

@keyframes pulse-record {
  0%, 100% {
    opacity: 0.8;
    transform: scale(1);
  }
  50% {
    opacity: 1;
    transform: scale(1.1);
  }
}

/* Recording mode toggle */
.settings-label {
  display: flex;
  align-items: center;
  cursor: pointer;
  margin-bottom: 8px;
}

.settings-hint {
  opacity: 0.7;
  font-size: 0.85em;
  font-weight: normal;
  margin-left: 8px;
}

/* In recording mode, make the fullscreen toggle more discreet */
.fullscreen-toggle.recording-mode {
  width: 36px;
  height: 36px;
  opacity: 0.6;
  background: rgba(0, 0, 0, 0.5);
}

/* Focus styles */
.segment-image-panel:focus,
.empty-state-placeholder:focus {
  box-shadow: 0 0 0 3px rgba(66, 153, 225, 0.5);
  outline: none;
}

/* =================== Original styles =================== */
.segment-image-panel {
  background-color: rgba(59, 130, 246, 0.1);
  border-radius: 8px;
  padding: 1px 16px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
  transition: all 0.3s ease;
  position: relative;
  overflow: hidden;
}

.image-display {
  cursor: pointer;
  padding: 0;
  position: relative;
}

.image-container {
  width: 100%;
  height: 100%;
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(0, 0, 0, 0.05);
  position: relative;
}

.image-animation-container {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
}

.document-image {
  width: 100%;
  height: 100%;
  object-fit: contain;
  transition: transform 0.3s ease;
}

.placeholder-image {
  width: 100%;
  max-width: 1200px;
  height: auto;
  border-radius: 8px;
  opacity: 0.7;
  margin-bottom: 16px;
}

.image-container img.fallback-image {
  object-fit: contain;  /* Good - prevents scrolling */
  opacity: 0.7;
  max-width: 100%;      /* Add this */
  max-height: 100%;     /* Add this */
  width: auto;          /* Add this */
  height: auto;         /* Add this */
}

.image-container:hover img {
  transform: scale(1.02);
}

.image-title {
  font-weight: 500;
  font-size: 14px;
  margin-bottom: 4px;
}

.image-source {
  font-size: 12px;
  opacity: 0.8;
}

.image-source a {
  color: white;
  text-decoration: none;
  transition: opacity 0.2s;
}

.image-source a:hover {
  opacity: 1;
  text-decoration: underline;
}

.image-error-message {
  position: absolute;
  bottom: 20px;
  left: 0;
  right: 0;
  text-align: center;
  background: rgba(0, 0, 0, 0.7);
  padding: 8px;
  color: white;
  font-size: 14px;
}

/* Empty state styling */
.empty-state-placeholder {
  background-color: rgba(0, 0, 0, 0.03);
  border-radius: 8px;
  padding: 0px 16px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
  text-align: center;
  transition: all 0.3s ease;
  animation: fadeSlideIn 0.3s ease-out;
  position: relative;
}

.placeholder-image-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  position: relative;
}

.placeholder-image-container .image-container {
  position: relative;
  width: 100%;
  display: flex;
  justify-content: center;
}

/* UI Controls Layer */
.ui-controls-layer {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: 10;
  pointer-events: none; /* Let clicks pass through by default */
  transition: opacity 0.5s ease;
}

/* Hide controls while playing */
.ui-controls-layer.hidden {
  opacity: 0;
  visibility: hidden;
  transition: opacity 0.5s ease, visibility 0s 0.5s;
}

/* Make buttons clickable */
.ui-controls-layer button {
  pointer-events: auto;
}

/* Fullscreen toggle button */
.fullscreen-toggle {
  position: absolute;
  top: 12px;
  right: 12px;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: rgba(28, 28, 28, 0.7);
  border: none;
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: background-color 0.2s ease, transform 0.2s ease;
  opacity: 0.7;
  z-index: 15;
}

.fullscreen-toggle:hover {
  background: rgba(60, 60, 60, 0.9);
  transform: scale(1.05);
  opacity: 1;
}

/* Large play button and navigation */
.large-play-button-container {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  pointer-events: auto;
}

.large-play-button {
  width: 80px;
  height: 80px;
  border-radius: 50%;
  background: rgba(45, 29, 74, 0.8);
  border: none;
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
  transition: transform 0.2s ease, background-color 0.2s ease;
  margin: 0 1rem;
}

.large-play-button:hover {
  transform: scale(1.1);
  background: rgba(66, 153, 225, 0.9);
}

.large-play-button svg {
  margin-left: 4px;
}

.nav-button {
  width: 50px;
  height: 50px;
  border-radius: 50%;
  background: rgba(45, 29, 74, 0.8);
  border: none;
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
  transition: transform 0.2s ease, background-color 0.2s ease;
}

.nav-button:hover {
  transform: scale(1.1);
  background: rgba(66, 153, 225, 0.9);
}

/* Settings toggle and panel */
.recording-settings {
  position: absolute;
  bottom: 20px;
  left: 20px;
  z-index: 15;
}

.settings-toggle {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: rgba(28, 28, 28, 0.7);
  border: none;
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: background-color 0.2s ease, transform 0.2s ease;
}

.settings-toggle:hover {
  background: rgba(60, 60, 60, 0.9);
  transform: scale(1.05);
}

.settings-panel {
  background: rgba(28, 28, 28, 0.85);
  border-radius: 8px;
  padding: 16px;
  width: 320px;
  color: white;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
  pointer-events: auto;
  margin-top: 8px;
}

.settings-section {
  margin-bottom: 16px;
}

.settings-section:last-child {
  margin-bottom: 0;
}

.settings-section label {
  display: block;
  margin-bottom: 8px;
  font-weight: 500;
}

.setting-buttons {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}

.setting-button {
  background: rgba(255, 255, 255, 0.1);
  border: none;
  color: white;
  padding: 6px 10px;
  font-size: 13px;
  border-radius: 4px;
  cursor: pointer;
  transition: all 0.2s;
}

.setting-button:hover {
  background: rgba(255, 255, 255, 0.2);
}

.setting-button.active {
  background: rgba(66, 153, 225, 0.6);
}

/* Animation options */
.animation-options {
  margin-top: 12px;
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.settings-checkbox {
  display: flex;
  align-items: center;
  cursor: pointer;
  user-select: none;
}

.settings-checkbox input[type="checkbox"] {
  margin-right: 8px;
  width: 16px;
  height: 16px;
}

/* Keyboard shortcuts overlay */
.keyboard-shortcuts-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.7);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 20;
  pointer-events: auto;
}

.keyboard-shortcuts-panel {
  background: rgba(40, 40, 40, 0.95);
  border-radius: 8px;
  padding: 24px;
  width: 360px;
  max-width: 90vw;
  color: white;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.4);
}

.keyboard-shortcuts-panel h3 {
  text-align: center;
  margin-top: 0;
  margin-bottom: 16px;
  font-size: 18px;
}

.shortcut-list {
  display: grid;
  grid-template-columns: auto 1fr;
  gap: 12px 16px;
  margin-bottom: 20px;
}

.shortcut-item {
  display: contents;
}

.key {
  display: inline-block;
  background: rgba(255, 255, 255, 0.15);
  border-radius: 4px;
  padding: 4px 8px;
  min-width: 24px;
  text-align: center;
  font-family: monospace;
  font-weight: bold;
}

.close-button {
  display: block;
  margin: 0 auto;
  background: rgba(66, 153, 225, 0.6);
  border: none;
  color: white;
  padding: 8px 16px;
  border-radius: 4px;
  cursor: pointer;
  transition: background-color 0.2s ease;
}

.close-button:hover {
  background: rgba(66, 153, 225, 0.8);
}

/* Animation transition helper */
.animation-transition {
  transition: transform 1s ease-in-out, opacity 1s ease-in-out;
}

/* Non-repeating animations */
.image-animation-container.no-repeat {
  animation-iteration-count: 1 !important;
  animation-fill-mode: forwards !important;
}

/* Animation keyframes and styles */
.zoom-pan-effect {
  animation: zoomPan 30s ease-in-out infinite;
}


.epic-reveal {
  animation: epicReveal 25s ease-in-out infinite;
}
.dramatic-focus {
  animation: dramaticFocus 3s ease-in-out infinite;
}
.aerial-sweep {
  animation: aerialSweep 22s ease-in-out infinite;
}
.directors-cut {
  animation: directorsCut 28s ease-in-out infinite;
}
.epic-pan {
  animation: epicPan 24s ease-in-out infinite;
}
.dolly-zoom {
  animation: dollyZoom 26s ease-in-out infinite;
}

.hollywood-dramatic {
  animation: hollywoodDramatic 30s ease-in-out infinite;
}

.ken-burns-effect {
  animation: kenBurns 15s ease-in-out infinite;
}

.fade-and-scale {
  animation: fadeScale 15s ease-in-out infinite;
}

.gentle-float {
  animation: gentleFloat 15s ease-in-out infinite;
}

.smooth-slide {
  animation: smoothSlide 15s ease-in-out infinite;
}

/* Modified keyframe animations for FullscreenImagePanel */
@keyframes zoomPan {
  0% {
    transform: scale(1) translate(0, 0);
  }
  25% {
    transform: scale(1.05) translate(-1%, -0.5%);
  }
  50%, 100% {
    transform: scale(1.1) translate(-2%, -1%);
  }
}

@keyframes kenBurns {
  0% {
    transform: scale(1.05) translate(-1%, -1%);
  }
  25% {
    transform: scale(1.09) translate(0%, 1%);
  }
  50%, 100% {
    transform: scale(1.12) translate(1%, 1%);
  }
}

@keyframes fadeScale {
  0% {
    opacity: 0.95;
    transform: scale(1);
  }
  50%, 100% {
    opacity: 1;
    transform: scale(1.08);
  }
}

@keyframes gentleFloat {
  0% {
    transform: translateY(0) scale(1.02);
  }
  50%, 100% {
    transform: translateY(-10px) scale(1.05);
  }
}

@keyframes smoothSlide {
  0% {
    transform: translateX(-2%) scale(1.05);
  }
  50%, 100% {
    transform: translateX(2%) scale(1.08);
  }
}

@keyframes hollywoodDramatic {
  0% {
    transform: scale(1) translate(0, 0) rotate(0deg);
    filter: brightness(0.9) contrast(0.9);
  }
  15% {
    transform: scale(1.08) translate(-1%, -1%) rotate(-0.5deg);
    filter: brightness(0.95) contrast(1.05);
  }
  35% {
    transform: scale(1.15) translate(2%, -2%) rotate(0.3deg);
    filter: brightness(1.05) contrast(1.15);
  }
  65% {
    transform: scale(1.25) translate(-3%, -1%) rotate(-0.2deg);
    filter: brightness(1.1) contrast(1.2);
  }
  85%, 100% {
    transform: scale(1.35) translate(1%, -3%) rotate(0.1deg);
    filter: brightness(1.15) contrast(1.25);
  }
}


@keyframes epicReveal {
  0% {
    transform: scale(0.8) translate(0, 0);
    filter: brightness(0.4) contrast(0.9) saturate(0.8);
    opacity: 0.7;
  }
  100% {
    transform: scale(1.15) translate(-2%, -4%);
    filter: brightness(1.1) contrast(1.35) saturate(1.15);
    opacity: 1;
  }
}

@keyframes dramaticFocus {
  0% {
    transform: scale(1.3) translate(-5%, -5%);
    filter: blur(8px) brightness(0.7) contrast(0.9);
    opacity: 0.8;
  }
  100% {
    transform: scale(1.1) translate(3%, -1%);
    filter: blur(0px) brightness(1.1) contrast(1.25);
    opacity: 1;
  }
}

@keyframes aerialSweep {
  0% {
    transform: scale(1.1) translate(-8%, -3%) rotate(-1deg);
    filter: brightness(0.9) contrast(1.1) saturate(0.9);
  }
  100% {
    transform: scale(1.3) translate(8%, -3%) rotate(1deg);
    filter: brightness(1.1) contrast(1.3) saturate(1.2);
  }
}


@keyframes directorsCut {
  0% {
    transform: scale(1) translate(0, 0);
    filter: grayscale(0.3) brightness(0.85) contrast(1.1);
  }
  20% {
    transform: scale(1.08) translate(-2%, -1%);
    filter: grayscale(0.2) brightness(0.9) contrast(1.15) sepia(0.1);
  }
  40% {
    transform: scale(1.16) translate(-4%, -2%);
    filter: grayscale(0.1) brightness(0.95) contrast(1.2) sepia(0.15);
  }
  60% {
    transform: scale(1.24) translate(-2%, -3%);
    filter: grayscale(0) brightness(1) contrast(1.25) sepia(0.2);
  }
  80% {
    transform: scale(1.32) translate(0%, -2%);
    filter: grayscale(0) brightness(1.05) contrast(1.3) sepia(0.15);
  }
  100% {
    transform: scale(1.4) translate(2%, -1%);
    filter: grayscale(0) brightness(1.1) contrast(1.35) sepia(0.1);
  }
}

@keyframes epicPan {
  0% {
    transform: scale(1.1) translateX(-5%);
    filter: brightness(0.9) contrast(1.1);
  }
  100% {
    transform: scale(1.3) translateX(5%);
    filter: brightness(1.1) contrast(1.3);
  }
}

@keyframes dollyZoom {
  0% {
    transform: scale(1) perspective(500px) translateZ(0px);
    filter: brightness(0.9);
  }
  100% {
    transform: scale(1.3) perspective(500px) translateZ(50px);
    filter: brightness(1.1);
  }
}

/* Mobile optimization */
@media (max-width: 768px) {
  .segment-image-panel, .empty-state-placeholder {
    padding: 12px;
    margin-bottom: 12px;
  }

  .image-display {
    padding: 0;
  }

  .image-container {
    max-height: calc(100vh - 150px);
  }

  .fullscreen-toggle, .settings-toggle {
    width: 36px;
    height: 36px;
  }

  .large-play-button {
    width: 60px;
    height: 60px;
  }

  .large-play-button svg {
    width: 36px;
    height: 36px;
  }

  .nav-button {
    width: 40px;
    height: 40px;
  }

  .settings-panel {
    width: 280px;
    padding: 12px;
  }

  .placeholder-image {
    max-width: 100%;
  }

  .large-record-button {
    width: 60px;
    height: 60px;
  }

  .large-record-button svg {
    width: 32px;
    height: 32px;
  }
}

/* Class for fullscreen state */
.segment-image-panel.is-fullscreen {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  width: 100vw;
  height: 100vh;
  padding: 0 !important;
  border-radius: 0;
  z-index: 9999;
  background-color: black;
  transition: none;
  overflow: hidden;
}

.empty-state-placeholder.is-fullscreen {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  width: 100vw;
  height: 100vh;
  padding: 0 !important;
  border-radius: 0;
  z-index: 9999;
  background-color: black;
  transition: none;
  overflow: hidden;
}

.is-fullscreen .image-container {
  max-height: none;
  height: 100vh;
  border-radius: 0;
}

.is-fullscreen .placeholder-image-container {
  width: 100%;
  height: 100%;
}

.is-fullscreen .document-image {
  max-height: 100vh;
}

.is-fullscreen .image-footer {
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  border-radius: 0;
}

/* iOS Safari fixes for fullscreen */
@supports (-webkit-touch-callout: none) {
  .segment-image-panel.is-fullscreen,
  .empty-state-placeholder.is-fullscreen {
    top: env(safe-area-inset-top, 0);
    bottom: env(safe-area-inset-bottom, 0);
    left: env(safe-area-inset-left, 0);
    right: env(safe-area-inset-right, 0);
    height: -webkit-fill-available;
  }
}

/* Aspect ratio classes */
.aspect-16-9 .image-container {
  aspect-ratio: 16/9;
}

.aspect-4-3 .image-container {
  aspect-ratio: 4/3;
}

.aspect-1-1 .image-container {
  aspect-ratio: 1/1;
}

.aspect-9-16 .image-container {
  aspect-ratio: 9/16;
}

.is-fullscreen .image-container {
  aspect-ratio: unset;
}

/* Screen capture optimizations - prevent flickering in recordings */
.segment-image-panel.is-fullscreen,
.empty-state-placeholder.is-fullscreen {
  backface-visibility: hidden; /* Prevent flickering */
  transform: translateZ(0); /* Force hardware acceleration */
  will-change: transform; /* Hint to browser for optimization */
}

/* Ensure smooth animation playback in fullscreen */
.segment-image-panel.is-fullscreen .image-animation-container,
.empty-state-placeholder.is-fullscreen .image-animation-container {
  backface-visibility: hidden;
  transform: translateZ(0);
  will-change: transform;
}

/* Fade transition animation */
@keyframes fade {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}

.fade-enter-active, .fade-leave-active {
  transition: opacity 0.3s;
}

.fade-enter, .fade-leave-to {
  opacity: 0;
}

/* Add or modify this CSS rule */
.is-fullscreen.is-playing .ui-controls-layer {
  opacity: 0 !important;
  visibility: hidden !important;
  pointer-events: none !important;
}

.intro-montage {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: 5;
  background-color: rgba(0, 0, 0, 0.8);
}

/* Montage image container */
.montage-image-container {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  opacity: 0;
  transition: opacity 0.7s ease-in-out;
  display: flex;
  align-items: center;
  justify-content: center;
}

/* Montage image */
.montage-image {
  width: 100%;
  height: 100%;
  object-fit: cover;
  object-position: center;
}

/* Montage transition */
.montage-transition-enter-active,
.montage-transition-leave-active {
  transition: opacity 0.5s;
}

.montage-transition-enter,
.montage-transition-leave-to {
  opacity: 0;
}

/* Add styles for empty state placeholder montage as well */
.empty-state-placeholder .intro-montage {
  z-index: 5;
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
}

/* Ensure montage images are properly sized in fullscreen */
.is-fullscreen .intro-montage {
  height: 100vh;
  width: 100vw;
}

.is-fullscreen .montage-image {
  height: 100vh;
  width: 100vw;
  object-fit: cover;
}

.image-footer {
  margin-top: 12px;
  padding: 12px 16px;
  background: rgba(0, 0, 0, 0.7);
  color: white;
  border-radius: 0 0 12px 12px;
  backdrop-filter: blur(8px);
  border-top: 1px solid rgba(255, 255, 255, 0.1);
}

.image-source {
  font-size: 12px;
  opacity: 0.9;
  margin-bottom: 8px;
}

.image-source a {
  color: #60A5FA;
  text-decoration: none;
  transition: color 0.2s;
}

.image-source a:hover {
  color: #93C5FD;
  text-decoration: underline;
}

/* Citation sources styles */
.citation-sources {
  border-top: 1px solid rgba(255, 255, 255, 0.15);
  padding-top: 8px;
  margin-top: 8px;
}

.citation-sources-label {
  font-size: 11px;
  opacity: 0.8;
  margin-bottom: 4px;
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.citation-sources-list {
  font-size: 12px;
  line-height: 1.4;
}

.citation-source-link {
  color: #34D399;
  text-decoration: none;
  transition: color 0.2s;
  opacity: 0.9;
}

.citation-source-link:hover {
  color: #6EE7B7;
  text-decoration: underline;
  opacity: 1;
}

.citation-separator {
  opacity: 0.6;
}

/* Mobile optimization */
@media (max-width: 768px) {
  .image-footer {
    padding: 10px 12px;
    font-size: 11px;
  }

  .citation-sources-list {
    font-size: 11px;
  }

  .citation-sources-label {
    font-size: 10px;
  }
}

/* Fullscreen adjustments */
.is-fullscreen .image-footer {
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  border-radius: 0;
  background: rgba(0, 0, 0, 0.8);
  backdrop-filter: blur(12px);
}

/* Animation for citation sources appearing */
.citation-sources {
  animation: slideInUp 0.3s ease-out;
}

@keyframes slideInUp {
  from {
    opacity: 0;
    transform: translateY(10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

  /* Font Settings Section CSS - Add this to your FullscreenImagePanel component styles */

/* Font Settings Section */
.font-settings-section {
  margin-bottom: 20px;
  border-top: 1px solid rgba(255, 255, 255, 0.1);
  padding-top: 16px;
}

.customization-label {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-bottom: 0.5rem;
  font-size: 0.875rem;
  color: #ffffff;
  font-weight: 500;
}

.customization-label svg {
  width: 16px;
  height: 16px;
  opacity: 0.8;
}

.font-control {
  margin-bottom: 12px;
}

.font-control:last-child {
  margin-bottom: 0;
}

.font-control label {
  display: block;
  margin-bottom: 6px;
  font-size: 13px;
  font-weight: 500;
  color: rgba(255, 255, 255, 0.9);
}

.font-select {
  width: 100%;
  background: rgba(255, 255, 255, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 4px;
  padding: 6px 8px;
  color: white;
  font-size: 13px;
  cursor: pointer;
  /* Ensure text color is white */
  -webkit-text-fill-color: white;
  -webkit-appearance: none;
  -moz-appearance: none;
  appearance: none;
}

.font-select:hover {
  background: rgba(255, 255, 255, 0.15);
  border-color: rgba(255, 255, 255, 0.3);
}

.font-select:focus {
  outline: none;
  border-color: rgba(66, 153, 225, 0.6);
  box-shadow: 0 0 0 2px rgba(66, 153, 225, 0.2);
  color: white;
}

.font-select option {
  background: #2d2d2d;
  color: white;
  padding: 4px 8px;
}

.range-control {
  display: flex;
  align-items: center;
  gap: 8px;
}

.font-range {
  flex: 1;
  height: 4px;
  background: rgba(255, 255, 255, 0.2);
  border-radius: 2px;
  outline: none;
  -webkit-appearance: none;
  cursor: pointer;
}

.font-range::-webkit-slider-thumb {
  -webkit-appearance: none;
  width: 16px;
  height: 16px;
  background: rgba(66, 153, 225, 0.8);
  border-radius: 50%;
  cursor: pointer;
  border: 2px solid white;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.3);
}

.font-range::-webkit-slider-thumb:hover {
  background: rgba(66, 153, 225, 1);
  transform: scale(1.1);
}

.font-range::-moz-range-thumb {
  width: 16px;
  height: 16px;
  background: rgba(66, 153, 225, 0.8);
  border-radius: 50%;
  cursor: pointer;
  border: 2px solid white;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.3);
}

.font-range::-moz-range-thumb:hover {
  background: rgba(66, 153, 225, 1);
  transform: scale(1.1);
}

.range-value {
  min-width: 40px;
  text-align: right;
  font-size: 12px;
  color: rgba(255, 255, 255, 0.8);
  font-family: monospace;
}

/* Font preset buttons styling */
.font-control .setting-buttons {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-top: 6px;
}

.font-control .setting-button {
  background: rgba(255, 255, 255, 0.1);
  border: none;
  color: white;
  padding: 4px 8px;
  font-size: 12px;
  border-radius: 4px;
  cursor: pointer;
  transition: all 0.2s;
  white-space: nowrap;
}

.font-control .setting-button:hover {
  background: rgba(255, 255, 255, 0.2);
}

.font-control .setting-button.active {
  background: rgba(66, 153, 225, 0.6);
  box-shadow: 0 1px 3px rgba(66, 153, 225, 0.3);
}

/* Enhanced color picker for font settings */
.color-picker-container {
  display: flex;
  align-items: center;
  gap: 0.75rem;
}

.current-color {
  width: 24px;
  height: 24px;
  border-radius: 4px;
  border: 1px solid rgba(255, 255, 255, 0.3);
  flex-shrink: 0;
}

.color-picker {
  -webkit-appearance: none;
  width: 32px;
  height: 32px;
  border: none;
  border-radius: 4px;
  overflow: hidden;
  cursor: pointer;
  background: transparent;
  flex-shrink: 0;
}

.color-picker::-webkit-color-swatch-wrapper {
  padding: 0;
}

.color-picker::-webkit-color-swatch {
  border: none;
  border-radius: 4px;
}

.reset-button {
  background: rgba(255, 255, 255, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 4px;
  padding: 0.25rem 0.5rem;
  font-size: 0.75rem;
  color: #ffffff;
  cursor: pointer;
  transition: all 0.2s;
  white-space: nowrap;
}

.reset-button:hover {
  background: rgba(255, 255, 255, 0.2);
  border-color: rgba(255, 255, 255, 0.3);
}

/* Specific overrides for font control color picker */
.font-control .current-color {
  width: 20px;
  height: 20px;
  border-radius: 3px;
  border: 1px solid rgba(255, 255, 255, 0.3);
  flex-shrink: 0;
}

.font-control .color-picker {
  width: 28px;
  height: 28px;
}

.font-control .reset-button {
  font-size: 11px;
  padding: 4px 8px;
}
</style>
